import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight, UserPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { prettyDate } from "@/lib/geo";
import { Avatar } from "./Avatar";
import { InviteTrip } from "./FriendHero";
import { useFriendActions } from "./useFriendActions";

const TEMPLATE = { dreams: "My bucketlist", top10: "My top 10", best: "My top 5 ever", year: "Year in review" };
const CHANNEL = { whatsapp: "WhatsApp", facebook: "Facebook", messenger: "Messenger", instagram: "Instagram", email: "Email", sms: "SMS", copy: "Copied message", native: "Phone share", other: "Your link" };
const via = (s) => (s.kind === "story" ? `${TEMPLATE[s.template]} ${s.format === "square" ? "post" : "story"}` : s.kind === "contact" ? `your invite to ${s.label}` : CHANNEL[s.kind]);

const PlanTrip = ({ p, me }) => {
  const { data } = useQuery({ queryKey: ["friend", p.user_id], queryFn: () => api.friend(p.user_id), retry: false });
  return data ? <InviteTrip friendId={p.user_id} name={p.name.split(" ")[0]} me={me} friend={data} compact /> : null;
};

const Actions = ({ p, me }) => {
  const A = useFriendActions();
  const btn = "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors";
  if (p.relation === "friend")
    return (
      <div className="flex items-center gap-2">
        <Link to={`/friend/${p.user_id}`} className={`${btn} border border-white/15 text-slate-200 hover:bg-white/10`} data-testid={`joined-view-${p.user_id}`}><ArrowUpRight size={13} /> View their list</Link>
        <PlanTrip p={p} me={me} />
      </div>
    );
  if (p.relation === "received")
    return <button onClick={() => A.accept(p)} className={`${btn} bg-emerald-400 text-[#04130C] hover:bg-emerald-300`} data-testid={`joined-accept-${p.user_id}`}><Check size={13} /> Accept friend request</button>;
  if (p.relation === "sent")
    return <span className="text-xs text-slate-500" data-testid={`joined-sent-${p.user_id}`}>Request sent</span>;
  return <button onClick={() => A.add(p)} className={`${btn} bg-sky-400 text-[#06121c] hover:bg-sky-300`} data-testid={`joined-add-${p.user_id}`}><UserPlus size={13} /> Add friend</button>;
};

export const JoinedCard = ({ me }) => {
  const { data } = useQuery({ queryKey: ["joined"], queryFn: api.joined, refetchInterval: 60000 });
  if (!data?.length) return null;
  return (
    <div className="mb-8 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04] p-5" data-testid="joined-card">
      <p className="eyebrow !text-emerald-300 flex items-center gap-1.5 mb-1"><Sparkles size={12} /> Joined from your links · {data.length}</p>
      <p className="text-xs text-slate-500 mb-4">People who made their own bucket list after opening one of your links.</p>
      <ul className="divide-y divide-white/5">
        {data.map((p) => (
          <li key={p.user_id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`joined-${p.user_id}`}>
            <span className="flex items-center gap-3 min-w-0">
              <Avatar f={p} size="w-9 h-9" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{p.name}</span>
                <span className="block text-[11px] text-slate-500" data-testid={`joined-via-${p.user_id}`}>Joined {prettyDate(p.at.slice(0, 10), "d MMM")} · via {via(p.source)}</span>
              </span>
            </span>
            <Actions p={p} me={me} />
          </li>
        ))}
      </ul>
    </div>
  );
};
